<template>
  <v-container>
    <v-row no-gutters>
      <v-col cols="12" sm="1">
        <router-link :to="{ name: 'InsuranceImportantInfo' }" class="back-arrow"
          ><v-icon large color="mid-grey" class="mt-1">{{
            icons.mdiArrowLeft
          }}</v-icon></router-link
        >
      </v-col>
      <v-col cols="12" sm="10">
        <h1>Debit order details</h1>
        <h3 v-if="isMonthlySubscription">
          Your premium will be collected via debit order on the selected day of
          each calendar month.
        </h3>
        <h3 v-else>
          Your premium will be collected via debit order on the next selected
          day of the month each year.
        </h3>
      </v-col>
    </v-row>
    <v-row no-gutters>
      <v-col cols="12" md="4" offset-md="1" class="mt-6">
        <v-img
          :src="
            require(`@/assets/${whitelabel.images.insuranceBankingDetailsPage}`)
          "
          aspect-ratio="1"
          alt="insurance banking details"
          class="insurance-img"
          :class="{ 'insurance-img-xs': $vuetify.breakpoint.xs }"
        />
      </v-col>
      <v-col cols="12" md="6" offset-md="1" class="mt-10">
        <h2 class="mb-3">Your banking details</h2>
        <ValidationObserver ref="observer">
          <v-form class="banking-details-form" @submit.prevent="submit">
            <v-row no-gutters>
              <v-col cols="12" md="6" class="mt-3">
                <h4>Account holder name</h4>
              </v-col>
              <v-col cols="12" md="4">
                <ValidationProvider
                  id="account_holder"
                  v-slot="{ errors }"
                  name="account_holder"
                  rules="required"
                >
                  <v-text-field
                    v-model.trim="bankDetails.accountHolderName"
                    :error-messages="errors"
                  />
                </ValidationProvider>
              </v-col>
            </v-row>

            <v-row no-gutters>
              <v-col cols="12" md="6" class="mt-3">
                <h4>Bank</h4>
              </v-col>
              <v-col cols="12" md="4">
                <ValidationProvider
                  id="bank"
                  v-slot="{ errors }"
                  name="bank"
                  rules="required"
                >
                  <v-select
                    v-model="bankDetails.bankName"
                    :items="banks"
                    :error-messages="errors"
                  />
                </ValidationProvider>
              </v-col>
            </v-row>

            <v-row no-gutters>
              <v-col cols="12" md="6" class="mt-3">
                <h4>Account number</h4>
              </v-col>
              <v-col cols="12" md="4">
                <ValidationProvider
                  id="account_number"
                  v-slot="{ errors }"
                  name="account_number"
                  rules="required|integer"
                >
                  <v-text-field
                    v-model.trim="bankDetails.accountNumber"
                    :error-messages="errors"
                  />
                </ValidationProvider>
              </v-col>
            </v-row>

            <v-row no-gutters>
              <v-col cols="12" md="6" class="mt-3">
                <h4>Preferred debit order date</h4>
              </v-col>
              <v-col cols="12" md="4">
                <ValidationProvider
                  id="debit_order_date"
                  v-slot="{ errors }"
                  name="debit_order_date"
                  rules="required|integer"
                >
                  <v-select
                    v-model="bankDetails.debitOrderDay"
                    :items="$options.debitOrderDays"
                    :error-messages="errors"
                    @change="calculateProRata"
                  />
                </ValidationProvider>
              </v-col>
            </v-row>

            <v-row v-if="bankDetails.debitOrderDay">
              <v-col cols="12" md="10" class="primary-box mt-1">
                <p class="pro-rata-amount">
                  A pro rata amount of R{{ proRata | currencyTwoDecimals }} will
                  be due upon activation of this policy.
                </p>
              </v-col>
            </v-row>

            <v-row class="my-sm-8 text-center text-sm-left">
              <v-col>
                <v-btn
                  v-if="!isLoggedInAsSomeoneElse"
                  rounded
                  depressed
                  large
                  color="primary"
                  type="submit"
                  :loading="buttonLoading"
                >
                  <span>Next</span
                  ><v-icon small>{{ icons.mdiArrowRight }}</v-icon>
                </v-btn>

                <v-btn
                  v-else
                  rounded
                  depressed
                  large
                  color="primary"
                  :loading="buttonLoading"
                  @click="sendToClientForApproval"
                >
                  Send to client for approval
                </v-btn>
              </v-col>
            </v-row>
          </v-form>
        </ValidationObserver>
      </v-col>
    </v-row>

    <ErrorSnackbar v-model="showError" />
  </v-container>
</template>

<script>
  import ErrorSnackbar from '../common/ErrorSnackbar.vue';
  import { formHelperMixin } from '../../mixins/form-helper-mixin';
  import { CREATE_OR_UPDATE_BANK_DETAILS_MUTATION } from '../../constants/graphql/insurance';
  import { MUTATION_SEND_TESTATOR_INSURANCE_APPROVAL_REQUEST } from '../../constants/graphql/emails';
  import { TESTATOR_QUERY } from '../../constants/graphql/testators';
  import { banks } from '../../constants/js/insurance';
  import {
    calculateMonthlyProRata,
    calculateYearlyProRata,
  } from '../../constants/js/pro-rata';

  export default {
    name: 'BankingDetails',
    components: {
      ErrorSnackbar,
    },
    mixins: [formHelperMixin],
    props: {
      testator: {
        required: true,
        type: Object,
      },
    },
    data() {
      return {
        bankDetails: {},
        banks: banks,
        proRata: null,
      };
    },
    computed: {
      isMonthlySubscription() {
        return this.testator.insurance.subscriptionType == 'MONTHLY';
      },
      insuranceAlertMessage() {
        return (
          this.testator.fullName +
          ' was sent a request to complete their policy'
        );
      },
    },
    created() {
      if (this.testator.insurance) {
        if (this.testator.insurance.bankDetails) {
          this.setExistingFields();
          this.calculateProRata();
        }
      } else {
        this.$router.push({ name: 'InsuranceExplanation' });
      }
    },
    methods: {
      submit() {
        this.$refs.observer.validate().then(valid => {
          if (valid) {
            this.buttonLoading = true;
            this.updateBankDetails();
          } else {
            const errors = this.$refs.observer.ctx.errors;
            document
              .getElementById(
                Object.keys(errors).find(field => errors[field].length)
              )
              .scrollIntoView({ block: 'center', behavior: 'smooth' });
            this.buttonLoading = false;
          }
        });
      },
      sendToClientForApproval() {
        this.buttonLoading = true;
        if (this.bankDetails.accountNumber) {
          this.submit();
        } else {
          this.sendApprovalRequest();
        }
      },
      updateBankDetails() {
        const bankDetailsInputDetails = {
          accountHolderName: this.bankDetails.accountHolderName,
          bankName: this.bankDetails.bankName,
          accountNumber: this.bankDetails.accountNumber,
          debitOrderDay: this.bankDetails.debitOrderDay,
          insuranceId: this.testator.insurance.id,
        };

        this.$apollo
          .mutate({
            mutation: CREATE_OR_UPDATE_BANK_DETAILS_MUTATION,
            variables: {
              bankDetailsInputDetails,
            },
            update: (cache, { data: { createOrUpdateBankDetails } }) => {
              this.updateCacheAfterUpdateBankDetails(
                cache,
                createOrUpdateBankDetails
              );
            },
          })
          .then(data => {
            if (this.isLoggedInAsSomeoneElse) {
              this.sendApprovalRequest();
            } else {
              this.$router.push({ name: 'InsurancePolicyConfirmation' });
            }
          })
          .catch(error => {
            this.displayError();
            throw error;
          });
      },
      updateCacheAfterUpdateBankDetails(cache, createOrUpdateBankDetails) {
        const query = {
          query: TESTATOR_QUERY,
          variables: { id: this.testatorId },
        };
        const data = cache.readQuery(query);

        data.testator.insurance.bankDetails = createOrUpdateBankDetails;

        cache.writeQuery({ ...query, data: data });
      },
      sendApprovalRequest() {
        const testatorId = this.testatorId;

        this.$apollo
          .mutate({
            mutation: MUTATION_SEND_TESTATOR_INSURANCE_APPROVAL_REQUEST,
            variables: {
              testatorId,
            },
          })
          .then(_response => {
            this.$router.push({
              name: 'OverviewPage',
              params: { insuranceAlertMessage: this.insuranceAlertMessage },
            });
          })
          .catch(error => {
            this.displayError();
            throw error;
          });
      },
      setExistingFields() {
        // using native JSON functions removes reactivity
        // so we can clone an object/variable without mutating the original source
        this.bankDetails.accountHolderName = JSON.parse(
          JSON.stringify(this.testator.insurance.bankDetails.accountHolderName)
        );
        this.bankDetails.bankName = JSON.parse(
          JSON.stringify(this.testator.insurance.bankDetails.bankName)
        );
        this.bankDetails.accountNumber = JSON.parse(
          JSON.stringify(this.testator.insurance.bankDetails.accountNumber)
        );
        this.bankDetails.debitOrderDay = JSON.parse(
          JSON.stringify(this.testator.insurance.bankDetails.debitOrderDay)
        );
      },
      calculateProRata() {
        const monthlyPayment = this.isMonthlySubscription
          ? this.testator.insurance.monthlyPremiumQuoted
          : this.testator.insurance.yearlyPremiumQuoted / 12;
        this.proRata = this.bankDetails.debitOrderDay
          ? calculateMonthlyProRata(
              monthlyPayment,
              this.bankDetails.debitOrderDay
            )
          : null;
      },
    },
    debitOrderDays: [
      1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21,
      22, 23, 24, 25, 26, 27, 28, 29, 30, 31,
    ],
  };
</script>

<style lang="scss" scoped>
  .banking-detail-img {
    max-width: 73%;
    margin: 12px auto 12px auto;
  }

  .banking-detail-img-xs {
    max-width: 48%;
    margin: 0 auto 20px auto;
  }

  .pro-rata-amount {
    padding: 10px 10px 0px 10px;
    font-size: 14px;
    text-align: center;
  }
</style>
